import { useState, useEffect, useRef } from 'react';

export default function Statistics() {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({ creators: 0, revenue: 0, growth: 0, satisfaction: 0 });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const targets = { creators: 35, revenue: 250, growth: 340, satisfaction: 98 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      
      setCounts({
        creators: Math.floor(targets.creators * progress),
        revenue: Math.floor(targets.revenue * progress),
        growth: Math.floor(targets.growth * progress),
        satisfaction: Math.floor(targets.satisfaction * progress)
      });

      if (step >= steps) {
        clearInterval(timer);
        setCounts(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [isVisible]);

  return (
    <section id="statistics" ref={sectionRef} className="bg-[#0F0F0F] py-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          <div className="text-center relative">
            <div className="mb-4">
              <p className="text-5xl lg:text-6xl font-serif bg-gradient-to-r from-[#F2A35E] to-[#F6C1A1] bg-clip-text text-transparent font-bold">
                {counts.creators}+
              </p>
            </div>
            <p className="text-white text-sm lg:text-base font-medium">Creators Managed</p>
            <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-16 bg-[#7B4A5A]/30"></div>
          </div>

          <div className="text-center relative">
            <div className="mb-4">
              <p className="text-5xl lg:text-6xl font-serif bg-gradient-to-r from-[#F2A35E] to-[#F6C1A1] bg-clip-text text-transparent font-bold">
                ${counts.revenue}K+
              </p>
            </div>
            <p className="text-white text-sm lg:text-base font-medium">Revenue Generated</p>
            <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-16 bg-[#7B4A5A]/30"></div>
          </div>

          <div className="text-center relative">
            <div className="mb-4">
              <p className="text-5xl lg:text-6xl font-serif bg-gradient-to-r from-[#F2A35E] to-[#F6C1A1] bg-clip-text text-transparent font-bold">
                {counts.growth}%
              </p>
            </div>
            <p className="text-white text-sm lg:text-base font-medium">Average Growth Rate</p>
            <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-16 bg-[#7B4A5A]/30"></div>
          </div>

          <div className="text-center">
            <div className="mb-4">
              <p className="text-5xl lg:text-6xl font-serif bg-gradient-to-r from-[#F2A35E] to-[#F6C1A1] bg-clip-text text-transparent font-bold">
                {counts.satisfaction}%
              </p>
            </div>
            <p className="text-white text-sm lg:text-base font-medium">Client Satisfaction</p>
          </div>
        </div>
      </div>
    </section>
  );
}
